'use client';

import { useEffect, useMemo, useState } from 'react';
import { PLACEMENTS_CATALOG, type PlacementDefinition } from '@/lib/catalogData';
import { mapPlacementRowToDefinition, type PlacementCatalogRow } from '@/lib/placementCatalog';
import { createClient } from '@/lib/supabase/client';

type UsePlacementCatalogResult = {
  placements: PlacementDefinition[];
  loading: boolean;
  source: 'database' | 'fallback';
};

/**
 * Loads placement catalog from DB and calculates real-time availability
 * by counting confirmed/paid orders for the given issue.
 * This ensures both the booking flow and admin dashboard use the same source of truth.
 */
export function usePlacementCatalog(issueId?: string): UsePlacementCatalogResult {
  const supabase = useMemo(() => createClient(), []);
  const [placements, setPlacements] = useState<PlacementDefinition[]>(PLACEMENTS_CATALOG);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState<'database' | 'fallback'>('fallback');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);

      // 1. Load placement catalog definitions
      const { data: catalogData, error: catalogError } = await supabase
        .from('placements')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (cancelled) return;

      let baseRows: PlacementCatalogRow[] = [];
      if (catalogError || !catalogData?.length) {
        // Fall back to static catalog
        setPlacements(PLACEMENTS_CATALOG);
        setSource('fallback');
        setLoading(false);
        return;
      }
      baseRows = catalogData as PlacementCatalogRow[];

      // 2. If an issueId is provided, calculate real booked slots from orders
      if (issueId) {
        const { data: ordersData } = await supabase
          .from('orders')
          .select('placement, regions')
          .eq('issue_id', issueId)
          .in('payment_status', ['paid', 'pending']);

        if (!cancelled && ordersData) {
          // Count booked slots per placement (each region in an order = 1 slot)
          const bookedCounts: Record<string, number> = {};
          for (const ord of ordersData) {
            const placementId = ord.placement as string;
            const regionCount = Array.isArray(ord.regions) ? ord.regions.length : 0;
            bookedCounts[placementId] = (bookedCounts[placementId] || 0) + regionCount;
          }

          const mapped = baseRows.map(row => {
            const def = mapPlacementRowToDefinition(row);
            // Override defaultFilled with real-time booked count
            const realFilled = bookedCounts[def.id] ?? 0;
            return { ...def, defaultFilled: realFilled };
          });

          if (!cancelled) {
            setPlacements(mapped);
            setSource('database');
            setLoading(false);
          }
          return;
        }
      }

      // 3. No issueId or orders query failed — use catalog default_filled
      if (!cancelled) {
        const mapped = baseRows.map(mapPlacementRowToDefinition);
        setPlacements(mapped);
        setSource('database');
        setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [supabase, issueId]);

  return { placements, loading, source };
}
